"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import { mediaQueries, useInViewConfig, useTransitionUpStyles } from "ui"
import { colors, sizes } from "ui/theme/theme"
import BlockWrapper from "../../common/BlockWrapper"
import CommonTitle from "../../common/bit/CommonTitle"
import BlockCardContainerWithShape from "./BlockCardContainerWithShape"
import FailureState from "./FailureState"
import OTPForm from "./OTPForm"
import PolicyForm, { FormData } from "./PolicyForm"
import SuccessState from "./SuccessState"
import usePolicyReviewEligibility from "./hooks/usePolicyReviewEligibility"

// Styled components

const StyledBlockWrapper = styled(BlockWrapper)`
  z-index: 2 !important;
`
const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 80px;
  padding-bottom: 64px;

  ${mediaQueries.desktop} {
    padding-top: 120px;
    padding-bottom: 120px;
  }
`

const StyledFormCard = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 764px;
  margin: 0 auto;
  padding: ${sizes.baseTriple};
  border-radius: 30px;
  background-color: ${colors.white};
  gap: ${sizes.baseDouble};
  box-shadow: -4px 8px 30px 0px rgba(2, 1, 64, 0.12);
  margin-top: 70px;
  ${mediaQueries.desktop} {
    padding: ${sizes.baseSix};
    margin-top: 70px;
  }
`
const StyledShapeWrapper = styled.div`
  display: none;
  ${mediaQueries.desktop} {
    display: block;
    position: absolute;
    right: 50px;
    top: -70px;
    width: 100%;
    z-index: -1;
    height: 622px;
    width: 629px;
    top: -70px;
  }
`

const StyledBlockCardContainerWithShape = styled(BlockCardContainerWithShape)`
  padding-bottom: 60px;
  padding-top: 80px;
  ${mediaQueries.desktop} {
    padding: 140px;
  }
`

// Main component
interface CopyOfPolicyBlockProps {
  data: any
  id?: string
}

const CopyOfPolicyBlock = ({ data, id }: CopyOfPolicyBlockProps) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)

  // Use the custom hook for policy review eligibility
  const {
    formState,
    phoneNumber,
    otpError,
    handleFormSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleRetry,
  } = usePolicyReviewEligibility(data)

  // Wrapper for form submission with explicit debugger
  const onFormSubmit = (formData: FormData) => {
    return handleFormSubmit(formData)
  }

  // Render different states
  if (formState === "success") {
    return (
      <StyledBlockCardContainerWithShape id={id}>
        <SuccessState data={data} />
      </StyledBlockCardContainerWithShape>
    )
  }

  if (formState === "error") {
    return (
      <StyledBlockCardContainerWithShape id={id}>
        <FailureState data={data} onRetry={handleRetry} />
      </StyledBlockCardContainerWithShape>
    )
  }

  return (
    <StyledBlockWrapper id={id}>
      <StyledContainer ref={ref} style={transitionStyles}>
        <CommonTitle
          title={data.title.value}
          subtitle={data.subtitle.value}
          titleComponent={"h1"}
        />
        <StyledFormCard>
          {(formState === "idle" || formState === "submitting") && (
            <PolicyForm
              data={data}
              onSubmit={onFormSubmit} // Using our new wrapper function
              isSubmitting={formState === "submitting"}
            />
          )}

          {(formState === "otpSent" ||
            formState === "otpVerifying" ||
            formState === "otpError" ||
            formState === "otpExpired") && (
            <OTPForm
              data={data}
              phoneNumber={phoneNumber}
              onSubmit={handleOtpSubmit}
              onResend={handleResendOtp}
              isSubmitting={formState === "otpVerifying"}
              error={otpError}
              numberOfDigits={5}
            />
          )}
          <StyledShapeWrapper>
            <Image
              fill
              src={"/formBackgroundShape.svg"}
              alt="form Background Shape"
            />
          </StyledShapeWrapper>
        </StyledFormCard>
      </StyledContainer>
    </StyledBlockWrapper>
  )
}

export default CopyOfPolicyBlock
